import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import api from 'utils/api';
import FormField from 'components/FormField';
import FormFieldHelperText from 'components/FormFieldHelperText';
import pxToRem from 'assets/theme/functions/pxToRem';
import localData from 'utils/localData';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { setSavedName } from 'utils/name';
import { Box, Typography, Button, TextField } from '@mui/material';
import { LANGUAGE, USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API } from 'shared/constants/apis';
import { getLanguageOptions, getPreferredTimezoneOptions } from './constants';
import KrillChat from 'krill';
import logger from 'utils/logger';
import { KrillChatAPI } from 'krill/lib/KrillChatAPI';
import { updateUserAttributes } from 'aws-amplify/auth';
import { TIMEZONE } from 'utils/formatTime';

// Create a logger instance for this component
const log = logger.createLogger('ProfileTab');

const propTypes = {
  userInfo: PropTypes.shape({
    firstName: PropTypes.string,
    username: PropTypes.string,
    userType: PropTypes.string,
    lastName: PropTypes.string,
    avataUrl: PropTypes.string,
    preferredLanguage: PropTypes.string,
    preferredTimezone: PropTypes.string,
  }),
};

const DashboardPersonalSettingsPreference = ({ userInfo }) => {
  const [showConfirmBeforeSend, setShowConfirmBeforeSend] = useState(false);
  const [warnUserNameEmpty, setWarnUserNameEmpty] = useState(false);
  const [formState, setFormState] = useState({
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    avataUrl: userInfo.avataUrl,
    preferredLanguage: userInfo.preferredLanguage,
    preferredTimezone: userInfo.preferredTimezone,
  });

  const preferredLanguage = localData.get(USER_DATA.PREFFERED_LANGUAGE);

  const onChangeFormValues = (key) => async (event) => {
    const newFormState = { ...formState, [key]: event.target.value };
    setFormState({ ...newFormState });
  };

  const onChangePreferredSettings = (key, newValue) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));
  };

  const handleUpdateClick = async () => {
    if (formState.firstName.length === 0 || formState.lastName.length === 0) {
      setWarnUserNameEmpty(true);
    } else {
      setShowConfirmBeforeSend(true);
    }
  };

  const handleConfirmClick = async () => {
    setShowConfirmBeforeSend(false);
    await sendToServer();
    localData.set('userPreferredLanguage', formState.preferredLanguage);
    KrillChat.Config.setConfig({
      CURRENT_LANGUAGE: formState.preferredLanguage,
    });
    await KrillChatAPI.setSelfUserInfo({
      nickname: `${formState.lastName} ${formState.firstName}`,
    });
  };

  const generateConfirmWindowContents = () => {
    const getPreferredLanguage = (lang) => {
      return getLanguageOptions().find((option) => option.id === lang)?.label;
    };

    const getPreferredTimezone = (tz) => {
      switch (tz) {
        case TIMEZONE.CHINA:
          return intl.get('shared_text_china_standard_time');
        case TIMEZONE.JAPAN:
          return intl.get('shared_text_japan_standard_time');
        default:
          return '';
      }
    };

    return (
      <Grid spacing={3} container style={{ marginTop: '-10px' }}>
        <Grid size={6}>
          <Typography variant="h6">
            {intl.get('personal_settings_label_first_name')}:{' '}
            {userInfo.firstName + ' ==> ' + formState.firstName}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="h6">
            {intl.get('personal_settings_label_last_name')}:{' '}
            {userInfo.lastName + ' ==> ' + formState.lastName}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="h6">
            {intl.get('personal_settings_label_preferred_language')}:{' '}
            {getPreferredLanguage(userInfo.preferredLanguage) +
              ' ==> ' +
              getPreferredLanguage(formState.preferredLanguage)}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="h6">
            {intl.get('personal_settings_label_preferred_timezone')}:{' '}
            {getPreferredTimezone(userInfo.preferredTimezone) +
              ' ==> ' +
              getPreferredTimezone(formState.preferredTimezone)}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const sendToServer = async () => {
    const variables = {
      data: {
        firstName: formState.firstName,
        lastName: formState.lastName,
        preferredLanguage: formState.preferredLanguage,
        preferredTimezone: formState.preferredTimezone,
      },
    };
    api.put(DOLPHIN_API.PERSONAL_INFO, variables).then(
      () => {
        setSavedName(formState.firstName, formState.lastName);
        // Updating the userInfo in the cognito
        updateUserAttributes({
          userAttributes: {
            locale: formState.preferredLanguage,
          },
        });
        window.location.reload();
      },
      (error) => {
        log.error('Error occurred', { error });
      }
    );
  };

  const getNameFields = () => {
    const firstNameField = (
      <TextField
        id="userFirstName"
        label={intl.get('personal_settings_label_first_name')}
        variant="outlined"
        value={formState.firstName}
        onChange={(e) => {
          if (e.target.value.length <= 17) {
            onChangeFormValues('firstName')(e);
            setWarnUserNameEmpty(false);
          }
        }}
        required
        error={warnUserNameEmpty && formState.firstName.length === 0}
        helperText={
          warnUserNameEmpty && formState.firstName.length === 0
            ? `${intl.get('personal_settings_label_first_name')} is required`
            : ''
        }
        fullWidth
      />
    );

    const lastNameField = (
      <TextField
        id="lastName"
        label={intl.get('personal_settings_label_last_name')}
        variant="outlined"
        value={formState.lastName}
        onChange={(e) => {
          if (e.target.value.length <= 17) {
            onChangeFormValues('lastName')(e);
            setWarnUserNameEmpty(false);
          }
        }}
        required
        error={warnUserNameEmpty && formState.lastName.length === 0}
        helperText={
          warnUserNameEmpty && formState.lastName.length === 0
            ? `${intl.get('personal_settings_label_last_name')} is required`
            : ''
        }
        fullWidth
      />
    );

    if (preferredLanguage === LANGUAGE.EN) {
      return (
        <Fragment>
          <Grid size={6}>
            <Box>{firstNameField}</Box>
          </Grid>
          <Grid size={6}>
            <Box>{lastNameField}</Box>
          </Grid>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Grid size={6}>
          <Box>{lastNameField}</Box>
        </Grid>
        <Grid size={6}>
          <Box>{firstNameField}</Box>
        </Grid>
      </Fragment>
    );
  };

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={5}
        justifyContent="center"
        sx={{ marginTop: 5, marginBottom: 3 }}>
        <Grid size={12}>
          <Box>
            <FormField
              id="username"
              name="username"
              label={intl.get('personal_settings_label_username')}
              variant="outlined"
              value={userInfo.username}
              disabled
              InputProps={{
                readOnly: true, // Make the input read-only
                style: { pointerEvents: 'none' }, // Disable pointer events
              }}
            />
          </Box>
        </Grid>
        {getNameFields()}
        <Grid size={6}>
          <Box>
            <Autocomplete
              id="select_preferred_language"
              value={
                getLanguageOptions().filter(
                  (value) => value.id === formState.preferredLanguage
                )[0]
              }
              onChange={(_, newValue) => {
                if (newValue && newValue.id)
                  onChangePreferredSettings('preferredLanguage', newValue.id);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={getLanguageOptions()}
              renderInput={(params) => (
                <FormField
                  {...params}
                  variant="outlined"
                  label={intl.get('personal_settings_label_preferred_language')}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <FormFieldHelperText
              helperText={intl.get(
                'personal_settings_helper_select_preferred_language'
              )}
            />
          </Box>
        </Grid>

        <Grid size={6}>
          <Box>
            <Autocomplete
              id="select_preferred_timezone"
              // The default for autocomplete should always be the first value
              value={
                getPreferredTimezoneOptions().filter(
                  (value) => value.id === formState.preferredTimezone
                )[0] ?? getPreferredTimezoneOptions()[0]
              }
              onChange={(event, newValue) => {
                if (newValue && newValue.id)
                  onChangePreferredSettings(USER_DATA.TIMEZONE, newValue.id);
              }}
              options={getPreferredTimezoneOptions()}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <FormField
                  {...params}
                  variant="outlined"
                  label={intl.get('personal_settings_label_preferred_timezone')}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <FormFieldHelperText
              helperText={intl.get(
                'personal_settings_helper_select_preferred_timezone'
              )}
            />
          </Box>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateClick}
            style={{
              color: 'white',
              fontSize: pxToRem(18),
            }}>
            {intl.get('personal_settings_button_update')}
          </Button>
        </Grid>
      </Grid>

      {/* Modals */}
      <ConfirmModal
        isOpened={showConfirmBeforeSend}
        onCancel={() => setShowConfirmBeforeSend(false)}
        onConfirm={handleConfirmClick}
        title={intl.get(
          'personal_settings_preference_modal_text_confirm_title'
        )}
        bodyText={intl.get(
          'personal_settings_preference_modal_text_confirm_hint'
        )}
        confirmButtonText={intl.get(
          'personal_settings_preference_modal_button_submit'
        )}
        cancelButtonText={intl.get(
          'personal_settings_preference_modal_button_cancel'
        )}>
        <Box>
          <Grid
            spacing={3}
            container
            justifyContent="flex-end"
            alignItems="flex-end">
            <Grid size={12}>
              {showConfirmBeforeSend && generateConfirmWindowContents()}
            </Grid>
          </Grid>
        </Box>
      </ConfirmModal>
    </>
  );
};

DashboardPersonalSettingsPreference.propTypes = propTypes;

export default DashboardPersonalSettingsPreference;
